import React, { useEffect } from 'react';
import { Form, Input, Button, Switch, InputNumber, Card, Tabs, message } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../../api/axios';
import { SaveOutlined, ArrowLeftOutlined } from '@ant-design/icons';

const { TextArea } = Input;

export const OlePageEdit: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const isEditing = Boolean(id);
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [form] = Form.useForm();

    const { data: pageData, isLoading } = useQuery({
        queryKey: ['adminOlePage', id],
        queryFn: async () => {
            if (!isEditing) return null;
            const res = await apiClient.get(`/admin/ole/pages/${id}`);
            return res.data;
        },
        enabled: isEditing
    });

    useEffect(() => {
        if (pageData && pageData.succeeded) {
            const pd = pageData.data;
            
            // Format translations for AntD Form (translations.de.title, etc.)
            const formattedTranslations: any = {};
            pd.translations?.forEach((t: any) => {
                formattedTranslations[t.languageCode] = {
                    title: t.title,
                    content: t.content,
                    seoDescription: t.seoDescription,
                    additionalData: t.additionalData
                };
            });

            form.setFieldsValue({
                slug: pd.slug,
                order: pd.order,
                isActive: pd.isActive,
                translations: formattedTranslations
            });
        }
    }, [pageData, form]);

    const mutation = useMutation({
        mutationFn: async (values: any) => {
            // Reformat translations array
            const translations = Object.keys(values.translations || {}).map(lang => ({
                languageCode: lang,
                title: values.translations[lang]?.title || '',
                content: values.translations[lang]?.content || '',
                seoDescription: values.translations[lang]?.seoDescription || '',
                additionalData: values.translations[lang]?.additionalData || ''
            }));

            const payload = {
                slug: values.slug,
                order: values.order,
                isActive: values.isActive,
                translations
            };

            if (isEditing) {
                return apiClient.put(`/admin/ole/pages/${id}`, { id, ...payload });
            } else {
                return apiClient.post('/admin/ole/pages', payload);
            }
        },
        onSuccess: () => {
            message.success(isEditing ? 'Sayfa güncellendi' : 'Sayfa oluşturuldu');
            queryClient.invalidateQueries({ queryKey: ['adminOlePages'] });
            navigate('/admin/ole/pages');
        },
        onError: (err: any) => {
            message.error(err?.response?.data?.detail || 'Hata oluştu');
        }
    });

    const onFinish = (values: any) => {
        mutation.mutate(values);
    };

    const tabItems = [
        { key: 'de', label: 'Almanca (DE)' },
        { key: 'en', label: 'İngilizce (EN)' },
        { key: 'tr', label: 'Türkçe (TR)' }
    ];

    if (isLoading) return <div>Yükleniyor...</div>;

    return (
        <Card title={isEditing ? "Sayfayı Düzenle" : "Yeni Sayfa Ekle"} extra={<Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/admin/ole/pages')}>Geri</Button>}>
            <Form form={form} layout="vertical" onFinish={onFinish} initialValues={{ isActive: true, order: 1 }}>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                    <Form.Item name="slug" label="Slug (URL Yolu)" rules={[{ required: true, message: 'Slug zorunludur' }]}>
                        <Input placeholder="ornek-sayfa" />
                    </Form.Item>
                    
                    <Form.Item name="order" label="Sıra" rules={[{ required: true }]}>
                        <InputNumber min={1} style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item name="isActive" label="Aktif mi?" valuePropName="checked">
                        <Switch />
                    </Form.Item>
                </div>

                <Tabs defaultActiveKey="de" items={tabItems.map(tab => ({
                    key: tab.key,
                    label: tab.label,
                    children: (
                        <div style={{ padding: '1rem 0' }}>
                            <Form.Item name={['translations', tab.key, 'title']} label="Sayfa Başlığı" rules={tab.key === 'de' ? [{ required: true, message: 'Almanca başlık zorunludur' }] : []}>
                                <Input placeholder={`${tab.key.toUpperCase()} Başlık`} />
                            </Form.Item>

                            <Form.Item name={['translations', tab.key, 'content']} label="İçerik">
                                <TextArea rows={10} placeholder={`${tab.key.toUpperCase()} İçerik`} />
                            </Form.Item>

                            <Form.Item name={['translations', tab.key, 'seoDescription']} label="SEO Açıklaması">
                                <TextArea rows={3} placeholder="Arama motorları için kısa açıklama" />
                            </Form.Item>

                            <Form.Item name={['translations', tab.key, 'additionalData']} label="Ek Veri (JSON)">
                                <TextArea rows={4} style={{ fontFamily: 'monospace' }} placeholder='{"heroImage": "/media/...", "sectionType": "hero"}' />
                            </Form.Item>
                        </div>
                    )
                }))} />

                <Form.Item>
                    <Button type="primary" htmlType="submit" loading={mutation.isPending} icon={<SaveOutlined />}>
                        {isEditing ? 'Güncelle' : 'Kaydet'}
                    </Button>
                </Form.Item>
            </Form>
        </Card>
    );
};
